/**
 * El flujo de captura de verde: las pantallas del POC, con
 * los copys y la mecánica validados en el prototipo y corregidos por la visita
 * del 5/9 (sin foto: la balanza no entrega ticket; hora de corte = hora de
 * campo; «Descarte» como segunda tarea de la estación).
 * Una pregunta por pantalla; se toca, no se escribe; avisar, no bloquear.
 */

import { generarUlid } from '../../src/ulid';
import { actualizarPreferencias } from '../datos/bd';
import { BorradorYaConfirmado, encolar, mutacionesDeVerde, type MutacionAEncolar } from '../datos/cola';
import { esImposible, esRaro, motivoImposible, netoG, rangoPlausibleKg, TECHO_BRUTO_KG } from '../nucleo/plausibilidad';
import { taraAplicable } from '../nucleo/tara';
import { cuandoCorto, fechaDeProduccion, horaCorta, HORA_MS, instanteLocal, MINUTO_MS } from '../nucleo/tiempo';
import { alternarPersona, armarPayloadCrear, brutoG, carroSinBajar, CARROS_POR_VIAJE, sumaTarasG, type Borrador } from '../nucleo/viaje';
import { padAnclado, botonMantener, chip, colorSeguro, el, flag, icono, kg, mil, PALETA, teclado, tile } from './componentes';
import { acciones, ahoraMs, app, conPersonaHabitual, descartarBorrador, guardarBorrador, irA, nuevoBorrador } from './estado';

/** Pasos por índice (el 6 era la foto y el 7 el fin de lote; ya no existen: v3 y v4.3). */
function pasosDe(): number[] {
	return [0, 1, 2, 3, 4, 5, 8];
}

const comprometiendo = new Set<string>();

/** El borrador queda DURABLE antes de cambiar de pantalla (A5 de la ronda). */
async function avanzar(borrador: Borrador): Promise<void> {
	const pasos = pasosDe();
	const siguiente = pasos[pasos.indexOf(borrador.paso) + 1];
	if (siguiente !== undefined) borrador.paso = siguiente;
	await guardarBorrador(borrador).catch((error) => console.error('no se pudo guardar el borrador', error));
	app.render();
}

async function retroceder(borrador: Borrador): Promise<void> {
	const pasos = pasosDe();
	const anterior = pasos[pasos.indexOf(borrador.paso) - 1];
	if (anterior !== undefined) borrador.paso = anterior;
	await guardarBorrador(borrador).catch((error) => console.error('no se pudo guardar el borrador', error));
	app.render();
}

export function pregunta(cuerpo: HTMLElement, titulo: string, sub?: string): void {
	const fila = el('div', { class: 'qrow' });
	fila.appendChild(el('div', { class: 'q' }, titulo));
	cuerpo.appendChild(fila);
	if (sub) cuerpo.appendChild(el('div', { class: 'qsub' }, sub));
}

function pie(pieNodo: HTMLElement, borrador: Borrador, derecho?: { texto: string; deshabilitado?: boolean; alTocar: () => void }): void {
	if (pasosDe().indexOf(borrador.paso) > 0) {
		const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver', type: 'button' });
		volver.appendChild(icono('volver', 24, 2.6));
		volver.onclick = () => retroceder(borrador);
		pieNodo.appendChild(volver);
	}
	if (derecho) {
		const boton = el('button', { class: 'btn go', type: 'button' }, derecho.texto);
		if (derecho.deshabilitado) boton.setAttribute('disabled', '');
		boton.onclick = derecho.alTocar;
		pieNodo.appendChild(boton);
	}
}

export function pintarCaptura(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	const pasos = pasosDe();
	// Un borrador guardado por una versión anterior puede traer un paso que ya no existe (el 7
	// del fin de lote, v4.3): sin esto quedaba en la revisión pero sin «Volver» ni puntos
	// (Codex, ronda del diff, 14/9). Cae en el último paso, que es la revisión.
	const ultimo = pasos[pasos.length - 1];
	if (ultimo !== undefined && !pasos.includes(borrador.paso)) borrador.paso = ultimo;
	const puntos = el('div', { class: 'dots' });
	// Un punto por paso: con uno de más, el último no se prendía ni en la revisión.
	for (let i = 0; i < pasos.length; i++) {
		const punto = el('i');
		if (pasos.indexOf(borrador.paso) >= i) punto.className = 'on';
		puntos.appendChild(punto);
	}
	cuerpo.appendChild(puntos);
	const contenido = el('div', { class: 'appbody' });
	cuerpo.appendChild(contenido);

	switch (borrador.paso) {
		case 0:
			return pQuien(contenido, pieNodo, borrador);
		case 1:
			return pLote(contenido, pieNodo, borrador);
		case 2:
			return pCarros(contenido, pieNodo, borrador);
		case 3:
			return pCorte(contenido, pieNodo, borrador);
		case 4:
			return pEntrada(contenido, pieNodo, borrador);
		case 5:
			return pKilos(contenido, pieNodo, borrador);
		default:
			return pRevisar(contenido, pieNodo, borrador);
	}
}

/** La grilla de personas, compartida con el descarte. `tope` = cuántas se pueden marcar. */
export function grillaPersonas(elegidas: string[], tope: number, alCambiar: () => void, estacion = 'verde', opciones: { soltarDuenio?: boolean } = {}): HTMLElement {
	// Solo las de la estación que pregunta (Nicolás, 13/9: «no deben haber gente de otras
	// estaciones»), la dueña del teléfono primero. La misma tabla sirve a las tres estaciones
	// porque cada una filtra por la suya; una persona ya elegida se muestra aunque no sea de
	// acá, para que un borrador viejo no esconda a quien firma.
	const duenio = app.sesion?.persona_habitual_id ?? null;
	const personas = (app.catalogos?.personas ?? [])
		.filter((p) => p.activo === 1 && (p.estacion_habitual === estacion || p.id === duenio || elegidas.includes(p.id)))
		.sort((a, b) => Number(b.id === duenio) - Number(a.id === duenio) || a.nombre.localeCompare(b.nombre));
	const grilla = el('div', { class: 'grid2' });
	personas.forEach((persona, indice) => {
		grilla.appendChild(
			tile({
				etiqueta: persona.nombre,
				badge: persona.id === duenio ? 'este teléfono' : estacion === 'verde' && persona.lleva_descarte === 1 ? 'lleva el descarte' : undefined,
				color: PALETA[indice % PALETA.length],
				avatarTexto: persona.nombre.slice(0, 1).toUpperCase(),
				seleccionado: elegidas.includes(persona.id),
				alTocar: () => {
					// El «¿quién?» del registro no suelta al dueño del teléfono; «¿quiénes embolsan?» sí: se
					// anota quién embolsó, no quién carga (boceto de embolsado; Codex, ronda de la v5 entera, 15/9).
					alternarPersona(elegidas, persona.id, tope, opciones.soltarDuenio ? null : duenio);
					alCambiar();
				},
			}),
		);
	});
	return grilla;
}

function pQuien(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	// Con el dueño del teléfono ya marcado, la pregunta que queda es por el compañero:
	// «Siguiente» viene habilitado, así que el copy tiene que ir a buscarlo. Si se sacó a
	// sí mismo —o el teléfono es compartido—, vuelve el copy de siempre.
	pregunta(
		cuerpo,
		'¿Quién descarga?',
		conPersonaHabitual(borrador.personas) ? 'Ya estás vos. Si vinieron de a dos, tocá al otro.' : 'Tocá tu nombre. Si vinieron de a dos, tocá los dos.',
	);
	cuerpo.appendChild(
		grillaPersonas(borrador.personas, 2, () => {
			void guardarBorrador(borrador);
			app.render();
		}),
	);
	// La segunda tarea de la estación (visita 5/9): el descarte de recepción se
	// pesa en la misma balanza y lo carga quien lo lleva. Este borrador de viaje
	// recién nace, así que se descarta sin perder nada. Las personas del viaje NO
	// pasan al descarte: lo firma quien lo lleva, no quien descargó (8/9).
	const descarte = el('button', { class: 'enlace', type: 'button' }, 'Descarte');
	descarte.onclick = async () => {
		// Acá también se llega con «cambiar» desde la revisión, con el viaje entero cargado:
		// ese no se borra —queda y retomar lo ofrece después—; solo el recién nacido (12/9).
		const vacio = borrador.lote_id === null && borrador.carros.length === 0 && borrador.bruto_kg === null;
		if (vacio) await descartarBorrador(borrador).catch(() => null);
		acciones.nuevoDescarte();
	};
	cuerpo.appendChild(descarte);
	pie(pieNodo, borrador, { texto: 'Siguiente', deshabilitado: borrador.personas.length === 0, alTocar: () => avanzar(borrador) });
}

function pLote(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	pregunta(cuerpo, '¿De qué lote viene?');
	// Un batch de blendeo (v5) no es un lote de campo: verde no lo ve, y el servidor lo rechaza igual.
	const lotes = (app.catalogos?.lotes_campo ?? []).filter((l) => l.activo === 1 && l.tipo !== 'blendeo');
	const ultimo = app.preferencias.ultimo_lote_id;
	lotes.sort((a, b) => Number(b.id === ultimo) - Number(a.id === ultimo) || a.nombre.localeCompare(b.nombre));
	const grilla = el('div', { class: 'grid2' });
	for (const lote of lotes) {
		grilla.appendChild(
			tile({
				etiqueta: lote.nombre,
				color: '#5A6B14',
				avatarIcono: 'lote',
				badge: lote.id === ultimo ? 'el último' : undefined,
				seleccionado: borrador.lote_id === lote.id,
				alTocar: () => {
					borrador.lote_id = lote.id;
					avanzar(borrador);
				},
			}),
		);
	}
	cuerpo.appendChild(grilla);
	pie(pieNodo, borrador);
}

function pCarros(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	pregunta(cuerpo, '¿Qué carros trajiste?', 'Tocá todos los que descargaste.');
	// La MISMA fecha que usa validarAntesDeEncolar: si acá se usara «ahora», un
	// borrador retomado después del corte de jornada ofrecería una tara que la
	// validación final rechaza, y el operario quedaría en un bucle.
	const fecha = fechaDeProduccion(borrador.entrada_ms ?? ahoraMs());
	const carros = (app.catalogos?.carros ?? []).filter((c) => c.activo === 1).sort((a, b) => a.numero - b.numero);
	const grilla = el('div', { class: 'grid2' });
	carros.forEach((carro, indice) => {
		const tara = taraAplicable(app.catalogos?.carros_tara ?? [], carro.id, fecha);
		const elegido = borrador.carros.find((c) => c.carro_id === carro.id);
		// v6 (§12.3): avisa si el carro no figura bajado desde su llegada anterior; no bloquea.
		const sinBajar = carroSinBajar(carro.id, app.catalogos?.carros_en_playa ?? [], app.viajesLocales);
		grilla.appendChild(
			tile({
				etiqueta: `Carro ${carro.numero}`,
				sub: tara ? `vacío ${kg(tara.tara_g)} kg${sinBajar ? ' · ⚠ no figura bajado' : ''}` : 'sin tara — avisale al encargado',
				color: colorSeguro(carro.color, PALETA[indice % PALETA.length]),
				avatarTexto: String(carro.numero),
				avatarCuadrado: true,
				seleccionado: Boolean(elegido),
				// Con dos ya elegidos, los demás se apagan: un tercero es un dedazo.
				deshabilitado: !tara || (!elegido && borrador.carros.length >= CARROS_POR_VIAJE),
				alTocar: () => {
					if (!tara) return;
					if (elegido) borrador.carros = borrador.carros.filter((c) => c.carro_id !== carro.id);
					else {
						// Snapshot CONGELADO acá (H6): un refresh de catálogos no lo mueve.
						borrador.carros.push({
							carro_id: carro.id,
							carro_tara_id: tara.id,
							tara_g_snapshot: tara.tara_g,
							numero: carro.numero,
							color: carro.color,
						});
						borrador.carros.sort((a, b) => a.numero - b.numero);
					}
					void guardarBorrador(borrador);
					app.render();
				},
			}),
		);
	});
	cuerpo.appendChild(grilla);
	// v6 (§12.3): los elegidos que no figuran bajados, dicho con el viaje; se puede seguir igual.
	for (const c of borrador.carros) {
		const sinBajar = carroSinBajar(c.carro_id, app.catalogos?.carros_en_playa ?? [], app.viajesLocales);
		if (!sinBajar) continue;
		cuerpo.appendChild(
			flag(
				'warn',
				`El carro ${c.numero} no figura bajado desde ${sinBajar.local ? 'un viaje de este teléfono que todavía no subió' : `el viaje ${sinBajar.nro_viaje ?? '—'}`}.`,
				'Se puede seguir igual: casi siempre es una bajada que no se anotó en producción.',
			),
		);
	}
	if (borrador.carros.length > 0) {
		const cantidad = borrador.carros.length;
		cuerpo.appendChild(
			flag(
				'ok',
				`${cantidad} carro${cantidad > 1 ? 's' : ''} · vacíos ${kg(sumaTarasG(borrador))} kg`,
				cantidad >= CARROS_POR_VIAJE
					? '— Un viaje trae uno o dos. Tocá uno para sacarlo si te equivocaste.'
					: '— Se resta solo cuando cargues el peso.',
			),
		);
	}
	pie(pieNodo, borrador, {
		texto: 'Siguiente',
		deshabilitado: borrador.carros.length === 0,
		alTocar: () => void avanzar(borrador),
	});
}

export function reloj(cuerpo: HTMLElement, ms: number, leyenda: string, alCambiar: (nuevoMs: number) => void): void {
	const caja = el('div', { class: 'clock' });
	// Un borrador retomado al día siguiente conserva la hora de ayer: se dice («ayer 14:20»), y «Ahora»
	// la trae al presente sin tocar el stepper noventa veces (Codex, ronda de la v5 entera, 15/9).
	caja.appendChild(el('div', { class: 't' }, cuandoCorto(instanteLocal(ms), ahoraMs())));
	caja.appendChild(el('div', { class: 'ago' }, leyenda));
	const pasos = el('div', { class: 'steppers' });
	for (const [texto, delta] of [
		['− 15 min', -15],
		['+ 15 min', 15],
	] as const) {
		const boton = el('button', { class: 'stp', type: 'button' }, texto);
		boton.onclick = () => alCambiar(ms + delta * MINUTO_MS);
		pasos.appendChild(boton);
	}
	const ahora = el('button', { class: 'stp', type: 'button' }, 'Ahora');
	ahora.onclick = () => alCambiar(ahoraMs());
	pasos.appendChild(ahora);
	caja.appendChild(pasos);
	cuerpo.appendChild(caja);
}

function pCorte(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	// Hora de campo (visita 5/9, r. 4): cuándo se cortó el pasto, no cuándo se pesó.
	pregunta(cuerpo, '¿A qué hora se cortó en el campo?', 'Si no sabés exacto, elegí lo más parecido.');
	const chips = el('div', { class: 'chips' });
	const opciones: Array<[string, number]> = [
		['Recién', 0],
		['Hace 1 hora', 1],
		['Hace 2 horas', 2],
		['Hace 3 horas', 3],
		['Hace 4 horas', 4],
		['Hace 6 horas', 6],
	];
	// Contra la ENTRADA al playón, no contra ahora: los chips se corrían con el reloj de
	// pared (el marcado se desmarcaba solo a los diez minutos) y en un borrador
	// retomado no coincidía ninguno (segunda pasada, 11/9).
	const base = borrador.entrada_ms ?? ahoraMs();
	for (const [texto, horas] of opciones) {
		const valor = base - horas * HORA_MS;
		// El chip elegido se ve marcado al volver del reloj (revisión de diseño, V7):
		// el POC comparaba minutos exactos y nunca coincidía. ±10 min es «ese chip».
		const elegido = borrador.corte_ms !== null && Math.abs(borrador.corte_ms - valor) <= 10 * 60_000;
		chips.appendChild(
			chip(texto, elegido, () => {
				borrador.corte_ms = valor;
				borrador.entrada_ms ??= ahoraMs();
				void guardarBorrador(borrador);
				app.render();
			}),
		);
	}
	cuerpo.appendChild(chips);
	if (borrador.corte_ms !== null) {
		const corteMs = borrador.corte_ms;
		reloj(cuerpo, corteMs, 'hora de corte', (nuevo) => {
			borrador.corte_ms = nuevo;
			void guardarBorrador(borrador);
			app.render();
		});
		// Lo que importa es cuánto pasó del corte a la entrada al playón, no a ahora: un
		// borrador retomado media jornada después no es un viaje que llegó tarde.
		if (base - corteMs > 6 * HORA_MS) {
			cuerpo.appendChild(flag('warn', 'Pasaron más de 6 horas', 'del corte a la entrada. Avisale al encargado.'));
		}
	}
	pie(pieNodo, borrador, { texto: 'Siguiente', deshabilitado: borrador.corte_ms === null, alTocar: () => avanzar(borrador) });
}

function pEntrada(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	borrador.entrada_ms ??= ahoraMs();
	pregunta(cuerpo, '¿A qué hora lo dejaste en el playón?');
	const esAhora = Math.abs(borrador.entrada_ms - ahoraMs()) < MINUTO_MS;
	reloj(cuerpo, borrador.entrada_ms, esAhora ? 'ahora' : 'corregido a mano', (nuevo) => {
		borrador.entrada_ms = nuevo;
		void guardarBorrador(borrador);
		app.render();
	});
	const volver = el('button', { class: 'chip ancho', type: 'button' }, 'Volver a ahora');
	volver.onclick = () => {
		borrador.entrada_ms = ahoraMs();
		void guardarBorrador(borrador);
		app.render();
	};
	cuerpo.appendChild(volver);
	pie(pieNodo, borrador, { texto: 'Siguiente', alTocar: () => avanzar(borrador) });
}

function pKilos(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	// Pad anclado (revisión de diseño 7/9/2026, V1): la pregunta y el lector quedan
	// fijos arriba, el teclado fijo sobre el pie, y todo lo condicional —barra,
	// avisos— vive en una franja del medio con scroll propio.
	const { arriba, medio } = padAnclado(cuerpo, pieNodo);
	pregunta(arriba, '¿Cuántos kilos marcó la balanza?', 'El peso con los carros cargados.');

	const tipeado = borrador.bruto_kg;
	const brutoViajeG = brutoG(borrador);
	const brutoViajeKg = brutoViajeG / 1000;
	const tarasG = sumaTarasG(borrador);
	// Ningún bruto real tiene menos de 4 dígitos: con 1–3 el operario todavía
	// está tipeando y los avisos serían ruido. Además, TODO lo condicional va
	// DEBAJO del teclado — el pad jamás se mueve bajo el dedo (bug cazado en el
	// teléfono real: el cartel de «peso imposible» al primer dígito empujaba el
	// teclado y el segundo toque caía en otra tecla).
	const terminoDeTipear = tipeado !== null && String(tipeado).length >= 4;

	const lector = el('div', { class: 'readout' });
	const grande = el('div', { class: 'big' });
	grande.appendChild(el('span', tipeado ? undefined : { class: 'apagado' }, tipeado ? mil(tipeado) : '0'));
	grande.appendChild(el('span', { class: 'unit' }, 'kg'));
	lector.appendChild(grande);
	if (borrador.carros.length > 0) {
		const cuenta = el('div', { class: 'calc' });
		cuenta.appendChild(el('span', undefined, `− carros vacíos ${kg(tarasG)} kg`));
		const derecha = el('span');
		const neto = netoG(brutoViajeKg, tarasG);
		derecha.appendChild(el('b', undefined, tipeado && neto > 0 ? `${kg(neto)} kg netos` : '—'));
		cuenta.appendChild(derecha);
		lector.appendChild(cuenta);
	}
	arriba.appendChild(lector);

	const motivo = terminoDeTipear ? motivoImposible(brutoViajeKg, tarasG) : null;
	const imposible = motivo !== null;
	const raro = terminoDeTipear && !imposible && esRaro(brutoViajeKg, borrador.carros.length);

	const pad =
		teclado(
			(digito) => {
				const actual = String(tipeado ?? '');
				if (actual.length >= 6) return;
				const nuevo = Number(`${actual}${digito}`);
				borrador.bruto_kg = nuevo;
				void guardarBorrador(borrador);
				app.render();
			},
			() => {
				const actual = String(tipeado ?? '');
				const nuevo = actual.length > 1 ? Number(actual.slice(0, -1)) : null;
				borrador.bruto_kg = nuevo;
				void guardarBorrador(borrador);
				app.render();
			},
		);

	// Todo lo que sigue va en la franja del medio: informa sin mover el pad. Con un
	// cartel a la vista la barra no va: repite el rango que el cartel ya dice (11/9).
	const cartel = imposible || raro;
	if (terminoDeTipear && !cartel) {
		const [minimo, maximo] = rangoPlausibleKg(borrador.carros.length);
		const alcance = maximo * 1.35;
		const barra = el('div', { class: 'bar' });
		const zona = el('span', { class: 'ok' });
		zona.style.left = `${(100 * minimo) / alcance}%`;
		zona.style.width = `${(100 * (maximo - minimo)) / alcance}%`;
		barra.appendChild(zona);
		const marca = el('span', { class: 'mk' });
		marca.style.left = `${Math.min(99, (100 * brutoViajeKg) / alcance)}%`;
		barra.appendChild(marca);
		medio.appendChild(barra);
		const etiquetas = el('div', { class: 'barlab' });
		etiquetas.appendChild(
			el(
				'span',
				undefined,
				`lo normal para ${borrador.carros.length} carro${borrador.carros.length > 1 ? 's' : ''}: ${mil(minimo)} – ${mil(maximo)} kg`,
			),
		);
		medio.appendChild(etiquetas);
	}
	if (motivo === 'techo') {
		// El techo del servidor (Codex, ronda del 6/10): seis cifras entran en el pad, y por encima de
		// 100.000 kg la mutación volvería rechazada. Se frena acá, con el mismo cartel que lo imposible.
		medio.appendChild(
			flag('stop', 'Ese peso no puede ser.', `Más de ${mil(TECHO_BRUTO_KG)} kg no se puede cargar. Fijate el número de la balanza.`),
		);
	} else if (motivo === 'taras') {
		medio.appendChild(
			flag('stop', 'Ese peso no puede ser.', `Los carros vacíos ya pesan ${kg(tarasG)} kg. Fijate el número de la balanza.`),
		);
	} else if (raro) {
		// Avisa, no bloquea (Agata, 6/10: dos carros pesaron 16.800 kg, «Siguiente» esperaba un «Sí, está
		// bien» y en la planta se cargó 14.800 como si fuera el tope; pidió sacar la restricción). El rango
		// de 2025 queda como información: lo que marcó la balanza se carga, y el tablero lo marca como
		// «carro pasado» por encima de los 4.000 kg por carro, que es lo único afirmable.
		medio.appendChild(
			flag(
				'warn',
				'¿Está bien ese número?',
				`Los otros viajes de ${borrador.carros.length} carro${borrador.carros.length > 1 ? 's' : ''} andan entre ${mil(rangoPlausibleKg(borrador.carros.length)[0])} y ${mil(rangoPlausibleKg(borrador.carros.length)[1])} kg. Si es lo que marcó la balanza, seguí; si no, corregilo con ⌫.`,
			),
		);
	}


	cuerpo.appendChild(pad);
	pie(pieNodo, borrador, {
		texto: 'Siguiente',
		// El bloqueo físico rige SIEMPRE (aunque el cartel espere a los 4 dígitos):
		// un número a medio tipear no avanza, y se completa solo al seguir tipeando.
		// Un bruto raro ya no frena (Agata, 6/10): avisa y deja seguir.
		deshabilitado: !tipeado || esImposible(brutoViajeKg, tarasG),
		alTocar: () => avanzar(borrador),
	});
}

export function nombreDe(personaId: string): string {
	return app.catalogos?.personas.find((p) => p.id === personaId)?.nombre ?? '—';
}

function resumenCarros(borrador: Borrador): string {
	return borrador.carros.map((c) => `N° ${c.numero}`).join(' + ');
}

function pRevisar(cuerpo: HTMLElement, pieNodo: HTMLElement, borrador: Borrador): void {
	pregunta(cuerpo, '¿Está todo bien?', 'Mirá los números antes de guardar.');
	const lote = app.catalogos?.lotes_campo.find((l) => l.id === borrador.lote_id)?.nombre ?? '—';
	const brutoViaje = brutoG(borrador);
	const neto = brutoViaje - sumaTarasG(borrador);
	const filas: Array<[Parameters<typeof icono>[0], string, string, number | null]> = [
		['persona', 'Quién', borrador.personas.map(nombreDe).join(' y '), 0],
		['lote', 'Lote', lote, 1],
		['carro', 'Carros', resumenCarros(borrador), 2],
		[
			'reloj',
			'Corte / playón',
			`${borrador.corte_ms ? horaCorta(borrador.corte_ms) : '—'} → ${borrador.entrada_ms ? horaCorta(borrador.entrada_ms) : '—'}`,
			3,
		],
		['peso', 'Bruto', `${kg(brutoViaje)} kg`, 5],
		['peso', 'Netos', `${kg(neto)} kg`, null], // derivado: se cambia el bruto
	];
	const resumen = el('div', { class: 'sum' });
	for (const [nombreIcono, etiqueta, valor, paso] of filas) {
		const fila = el('div', { class: 'r' });
		const cajaIcono = el('div', { class: 'ic' });
		cajaIcono.appendChild(icono(nombreIcono, 19));
		fila.appendChild(cajaIcono);
		const textos = el('div');
		textos.appendChild(el('div', { class: 'lb' }, etiqueta));
		textos.appendChild(el('div', { class: 'vl' }, valor));
		fila.appendChild(textos);
		if (paso !== null) {
			const cambiar = el('button', { class: 'fix', type: 'button' }, 'cambiar');
			cambiar.onclick = () => {
				borrador.paso = paso;
				void guardarBorrador(borrador);
				app.render();
			};
			fila.appendChild(cambiar);
		}
		resumen.appendChild(fila);
	}
	cuerpo.appendChild(resumen);

	// Nota opcional (paridad con el Excel: 0 usos esperados; texto libre SOLO acá).
	if (borrador.nota === null) {
		const agregar = el('button', { class: 'enlace', type: 'button' }, 'agregar una nota');
		agregar.onclick = () => {
			borrador.nota = '';
			app.render();
		};
		cuerpo.appendChild(agregar);
	} else {
		const nota = el('textarea', { class: 'nota', rows: '2', maxlength: '400', placeholder: 'Nota para la oficina…' });
		nota.value = borrador.nota;
		nota.oninput = () => {
			borrador.nota = nota.value;
			void guardarBorrador(borrador);
		};
		cuerpo.appendChild(nota);
	}

	const volver = el('button', { class: 'btn ghost', 'aria-label': 'Volver', type: 'button' });
	volver.appendChild(icono('volver', 24, 2.6));
	volver.onclick = () => retroceder(borrador);
	pieNodo.appendChild(volver);
	pieNodo.appendChild(botonMantener('Mantené apretado', 1500, () => void confirmarCaptura(borrador)));
}

/**
 * Cinturón previo al encolado (C2/A9 de la ronda del diff): el bruto de cada
 * viaje debe superar sus taras, y cada tara congelada debe seguir siendo LA
 * aplicable para el día de producción que el servidor va a derivar de la
 * entrada (puede diferir del día en que se eligieron los carros si cruzó el
 * corte de jornada o hubo recalibración). Si algo no cierra, se vuelve al paso
 * de carros.
 */
function validarAntesDeEncolar(borrador: Borrador): string | null {
	const motivo = motivoImposible(brutoG(borrador) / 1000, sumaTarasG(borrador));
	if (motivo === 'taras') {
		return 'El peso no cubre los carros vacíos.';
	}
	if (motivo === 'techo') {
		// Un borrador guardado antes del techo en la pantalla puede traerlo (Codex, ronda del 6/10).
		return `El peso supera los ${mil(TECHO_BRUTO_KG)} kg que se pueden cargar.`;
	}
	// La grilla apaga el tercer carro, pero un borrador guardado ANTES de ese tope
	// puede traer tres y llegar a la revisión sin pasar por la grilla (Codex, ronda
	// del 9/9). No se recorta en silencio: se pide corregir la selección.
	if (borrador.carros.length > CARROS_POR_VIAJE) {
		return `Un viaje trae hasta ${CARROS_POR_VIAJE} carros. Volvé a elegirlos.`;
	}
	const fechaFinal = fechaDeProduccion(borrador.entrada_ms ?? ahoraMs());
	for (const carro of borrador.carros) {
		const aplicable = taraAplicable(app.catalogos?.carros_tara ?? [], carro.carro_id, fechaFinal);
		if (!aplicable || aplicable.id !== carro.carro_tara_id || aplicable.tara_g !== carro.tara_g_snapshot) {
			return 'Cambió la tara de un carro. Volvé a elegir los carros.';
		}
	}
	return null;
}

async function confirmarCaptura(borrador: Borrador): Promise<void> {
	if (comprometiendo.has(borrador.capture_id)) return;
	comprometiendo.add(borrador.capture_id);
	try {
		// La campaña es la del borrador (fijada al crearlo); el catálogo actual solo
		// entra si el borrador nació sin campaña conocida.
		const campania = borrador.campania_id ?? campaniaAbierta();
		const creadoEn = new Date().toISOString();
		const problema = validarAntesDeEncolar(borrador);
		if (problema) {
			alert(`No se guardó: ${problema}`);
			borrador.paso = 2;
			await guardarBorrador(borrador);
			app.render();
			return;
		}
		await encolar(app.bd, [aMutacion(borrador, campania)], [borrador.capture_id], creadoEn);
		// v6 (§12.3): el viaje recién encolado cuenta ya para «no figura bajado» del siguiente.
		app.viajesLocales = await mutacionesDeVerde(app.bd, app.preferencias.catalogos_hora_servidor);

		if (borrador.lote_id) {
			app.preferencias.ultimo_lote_id = borrador.lote_id;
			void actualizarPreferencias(app.bd, { ultimo_lote_id: borrador.lote_id });
		}
		irA({
			tipo: 'guardado',
			entidad: 'viaje_verde',
			registroId: borrador.viaje_id,
			netoG: brutoG(borrador) - sumaTarasG(borrador),
			nroViaje: null,
			enviado: false,
		});
		app.dispararSync();
	} catch (error) {
		if (error instanceof BorradorYaConfirmado) {
			// Una copia vieja de este borrador en otra pestaña: ya se mandó; no se manda dos veces.
			await descartarBorrador(borrador).catch(() => null);
			alert('Ese viaje ya se mandó desde otra pestaña: no se mandó dos veces.');
			acciones.nuevaCaptura();
			return;
		}
		console.error('no se pudo guardar la captura', error);
		alert('No se guardó. Revisá y probá de nuevo; si sigue fallando, avisale al encargado.');
		app.render();
	} finally {
		comprometiendo.delete(borrador.capture_id);
	}
}

export function campaniaAbierta(): string {
	const abiertas = (app.catalogos?.campanias ?? []).filter((c) => c.fecha_fin === null);
	const elegida = abiertas.sort((a, b) => b.anio - a.anio)[0];
	if (!elegida) throw new Error('no hay campaña abierta en los catálogos');
	return elegida.id;
}

function aMutacion(borrador: Borrador, campaniaId: string): MutacionAEncolar {
	const payload = armarPayloadCrear(borrador, campaniaId);
	return {
		id: borrador.mutacion_id,
		tipo: 'viaje_verde.crear',
		payload,
		entidad: 'viaje_verde',
		registro_id: borrador.viaje_id,
		personas: [...borrador.personas],
		resumen: {
			entidad: 'viaje_verde',
			// La misma regla que el servidor: el día de producción sale de la entrada.
			fecha: fechaDeProduccion(borrador.entrada_ms ?? ahoraMs()),
			// La hora declarada, para que «Verde, últimas 24 horas» ordene y recorte lo propio (§5.5).
			entrada_en: payload.entrada_en,
			lote: app.catalogos?.lotes_campo.find((l) => l.id === borrador.lote_id)?.nombre ?? '—',
			carros: resumenCarros(borrador),
			cant_carros: borrador.carros.length,
			bruto_g: payload.bruto_g,
			neto_g: payload.bruto_g - sumaTarasG(borrador),
		},
	};
}
